import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import HomeSlider from "../Home/homeSlider";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
  ThreeColRow,
  StepCard,
  StepCardIcon,
  StepCardTitle,
  StepCardDesc,
  ServiceCardGrid,
  ServiceCard,
  ServiceCardImg,
  ServiceCardTitle,
  ServiceCardDesc,
} from "../../components/ContentLayouts";

// ── Image imports ─────────────────────────────────────────────────────────────
import imgWhatToLookFor       from "../../assets/images/content/pet-clinic-bangalore/what-to-look-for.jpg";
import iconHowToBook1         from "../../assets/images/icons/location.png";
import iconHowToBook2         from "../../assets/images/icons/review-list.png";
import iconHowToBook3         from "../../assets/images/icons/med-calendar.png";
import imgConsultations       from "../../assets/images/content/pet-clinic-bangalore/services-consultations.jpg";
import imgVaccinations        from "../../assets/images/content/pet-clinic-bangalore/services-vaccinations.jpg";
import imgGrooming            from "../../assets/images/content/pet-clinic-bangalore/services-grooming.jpg";
import imgDiagnostics         from "../../assets/images/content/pet-clinic-bangalore/services-diagnostics.jpg";

const faqs = [
  {
    question: "Is PetQura available in my area?",
    answer:
      "Yes, our platform covers various neighborhoods across the city, helping you find a reliable pet clinic in Bangalore near your specific location.",
  },
  {
    question: "How do I find the nearest PetQura clinic?",
    answer:
      "Simply enter your location and the required service on our website or our app, and we will display the closest veterinary hospitals in Bangalore that match your needs.",
  },
  {
    question: "What services are available at this location?",
    answer:
      "Services vary by clinic, but you can typically find routine consultations, vaccinations, grooming, and diagnostics at any Bangalore pet clinic listed on our platform.",
  },
  {
    question: "What are the operating hours of this clinic?",
    answer:
      "Operating hours depend on the specific pet clinic in Bangalore you choose to visit. You can view the exact timings for each clinic directly on their profile before you book.",
  },
  {
    question: "Can I book an appointment in any hospital?",
    answer:
      "Absolutely. You can easily book pet services online in any listed veterinary hospitals in Bangalore by selecting an available time slot through our secure website or app.",
  },
  {
    question: "Do you verify the veterinarians on the platform?",
    answer:
      "Yes, we prioritize connecting you with the best veterinary hospitals in Bangalore, ensuring your pet is treated by trusted, expert, and qualified professionals.",
  },
  {
    question: "What should I do in a pet emergency?",
    answer:
      "If your pet is experiencing a crisis, use our search feature to instantly locate the nearest open clinic and secure an emergency vet appointment so they can prepare for your pet's arrival.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetClinicBangalore = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Find Pet Clinics in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Looking for a pet clinic in Bangalore? Browse verified veterinary hospitals and veterinary clinics across the city, compare ratings and book pet services online in just a few clicks."
        />
      </Helmet>

      <HomeSlider page={"clinic"} />

      <PageWrapper>
        <HeroSection>
          <h1>Pet Clinic in Bangalore by PetQura</h1>
          <p>
            As a pet parent in a bustling city, keeping your furry family member
            healthy and happy is always a top priority. We understand that when
            your companion needs medical attention, navigating heavy traffic to
            find a reliable pet clinic in Bangalore shouldn't add to your
            stress. You need quick, accessible, and compassionate care right in
            your neighborhood.
          </p>
          <p>
            That is why PetQura is designed to help you easily locate and
            connect with various trusted Bangalore pet clinic near you without
            the usual hassle. Our platform ensures that you can quickly find the
            exact veterinary services you need. We focus on connecting you with
            expert and trusted vets, ensuring your pet gets the prompt attention
            they deserve while saving you time and energy.
          </p>
          <EmbeddedClinicList defaultLocation={{ latitude: 12.9716, longitude: 77.5946 }} previewMode />
        </HeroSection>

        {/* ── What to Look For — Pattern A (text left, image right) ── */}
        <ContentSection>
          <SectionHeading>
            What to Look For in Veterinary Hospitals in Bangalore
          </SectionHeading>
          <TwoColRow>
            <ColText>
              <p>
                When searching for veterinary hospitals in Bangalore, it is
                important to know what makes a facility reliable and safe for
                your pet. The well-being of your companion depends heavily on a
                clean, stress-free environment and highly qualified
                professionals.
              </p>
              <BulletList>
                <li>
                  First, always look for hospitals that maintain strict hygiene
                  standards to prevent any risk of infections.
                </li>
                <li>
                  Second, ensure the clinic has experienced veterinarians who
                  can clearly and calmly explain your pet's health to you.
                </li>
                <li>
                  Finally, the most reliable veterinary hospitals in Bangalore
                  offer a comprehensive range of services under one roof, from
                  routine check-ups to advanced diagnostics.
                </li>
              </BulletList>
              <p>
                At PetQura, we keep all these essential factors in mind when
                helping you find the right care center for your pet.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgWhatToLookFor}
                alt="What to look for in veterinary hospitals in Bangalore"
              />
            </ColImage>
          </TwoColRow>
        </ContentSection>

        {/* ── How to Book — Pattern C (intro / 3-col steps / closing) ── */}
        <ContentSection>
          <SectionHeading>
            How to Book Pet Services Online in Bangalore
          </SectionHeading>
          <p>
            We believe that scheduling a vet visit should be simple and
            stress-free. With PetQura, appointments can be effortlessly taken
            with the best veterinary clinic in Bangalore using your smartphone
            or computer. The process is clear, logical, and designed for your
            convenience:
          </p>
          <ThreeColRow>
            <StepCard>
              <StepCardIcon src={iconHowToBook1} alt="Enter location" />
              <StepCardTitle>Enter Location &amp; Service</StepCardTitle>
              <StepCardDesc>
                Enter desired location and select the specific service your pet
                needs.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconHowToBook2} alt="Review clinics" />
              <StepCardTitle>Review &amp; Choose Clinic</StepCardTitle>
              <StepCardDesc>
                Review list of nearby veterinary experts and choose the clinic
                that best fits your requirements.
              </StepCardDesc>
            </StepCard>
            <StepCard>
              <StepCardIcon src={iconHowToBook3} alt="Book appointment" />
              <StepCardTitle>Select Time &amp; Confirm</StepCardTitle>
              <StepCardDesc>
                Select your time slot and confirm the appointment with just a
                few clicks.
              </StepCardDesc>
            </StepCard>
          </ThreeColRow>
          <p>
            When you find the best veterinary hospitals in Bangalore through our
            platform, you can skip the long waiting room queues. This ensures
            that the clinic is ready for your arrival, making the entire
            experience smoother and far more relaxed for both you and your pet.
          </p>
        </ContentSection>

        <ContentSection>
          <SectionHeading>
            Why We Connect You to the Best Pet Clinic in Bangalore
          </SectionHeading>
          <p>
            We know you want nothing less than the best pet clinic in Bangalore
            for your companion. PetQura simplifies this process by combining
            online convenience with offline, hands-on care. We bridge the gap
            between pet parents and top-rated local veterinary experts, taking
            the guesswork out of finding reliable medical help.
          </p>
          <p>
            Whether you need a quick vaccination update or a detailed health
            consultation, our platform helps you identify the best veterinary
            clinic in Bangalore near you based on your specific location and
            service needs. By filtering your options based on proximity and
            specialty, we ensure you spend less time searching and more time
            caring for your pet. Our objective approach focuses strictly on
            trusted, verified clinics that meet high standards of animal care.
          </p>
        </ContentSection>

        {/* ── Services Available — Pattern E (intro / 2-col cards / 2-col cards) ── */}
        <ContentSection>
          <SectionHeading>
            Services Available at a Bangalore Pet Clinic Near You
          </SectionHeading>
          <p>
            Every pet has unique needs, which is why a good pet clinic in
            Bangalore must offer a variety of essential services. Through
            PetQura, you can easily find clinics that provide comprehensive care
            tailored to your pet's life stage, breed, and current health
            condition near you. Our partner clinics typically offer the
            following essential services:
          </p>
          <ServiceCardGrid>
            <ServiceCard>
              <ServiceCardImg
                src={imgConsultations}
                alt="Routine Consultations"
              />
              <ServiceCardTitle>Routine Consultations</ServiceCardTitle>
              <ServiceCardDesc>
                General health check-ups, behavioral advice, and nutritional
                guidance from expert vets to keep your pet in top shape.
              </ServiceCardDesc>
            </ServiceCard>
            <ServiceCard>
              <ServiceCardImg src={imgVaccinations} alt="Vaccinations" />
              <ServiceCardTitle>Vaccinations</ServiceCardTitle>
              <ServiceCardDesc>
                Timely immunization schedules designed to protect your dogs and
                cats against common, preventable diseases.
              </ServiceCardDesc>
            </ServiceCard>
          </ServiceCardGrid>
          <ServiceCardGrid>
            <ServiceCard>
              <ServiceCardImg src={imgGrooming} alt="Pet Grooming" />
              <ServiceCardTitle>Pet Grooming</ServiceCardTitle>
              <ServiceCardDesc>
                Professional cleaning, bathing, nail trimming, and coat care to
                keep your pet comfortable and hygienic.
              </ServiceCardDesc>
            </ServiceCard>
            <ServiceCard>
              <ServiceCardImg src={imgDiagnostics} alt="Diagnostics" />
              <ServiceCardTitle>Diagnostics</ServiceCardTitle>
              <ServiceCardDesc>
                Blood tests, X-rays, and other essential lab services for
                accurate and swift health assessments.
              </ServiceCardDesc>
            </ServiceCard>
          </ServiceCardGrid>
        </ContentSection>

        <ContentSection>
          <SectionHeading>
            Our Network of Veterinary Clinics in Bangalore
          </SectionHeading>
          <p>
            Our goal is to ensure that no matter where you live in the city,
            you have access to quality care in Bangalore near you. PetQura
            helps you discover nearby veterinary clinics in Bangalore across
            various neighborhoods. From the busy streets of Indiranagar and
            Koramangala to the residential family hubs of Jayanagar and
            Whitefield, we help you find a facility just around the corner.
          </p>
          <p>
            Because we focus on location-based matching, you can avoid long,
            stressful car rides that often make pets highly anxious. Finding
            reliable veterinary clinics in Bangalore has never been more
            straightforward, allowing you to prioritize your pet's comfort and
            prompt medical attention over long commutes.
          </p>
        </ContentSection>

        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetClinicBangalore;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.25rem;
    font-family: Montserrat, sans-serif;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
